import React, {useEffect, useState} from 'react';
import {ActivityIndicator, Image, Pressable, ScrollView, StyleSheet, View} from 'react-native';
import {useFocusEffect} from '@react-navigation/native';
import {observer} from 'mobx-react-lite';

import PromotionProductListing from '../../components/PromotionProductListing';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {useTabBarBottomPadding} from '../../hooks/useTabBarBottomPadding';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {AirtablePromotionItem} from '../../models/airtable';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {usePromotions} from '../../queries/promotions';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {OrderLine} from '../../transport/apiServiceResponse';
import {getFeedDiscoverDetailGraphics} from '../../utils/feedAndDiscover/media';
import {getHeroImageDetail} from '../../utils/heroImage';
import {logger, serializeError} from '../../utils/logger';

const HEADLINE_VIDEO = 'HEADLINE_VIDEO';

const BrandDetailContent = observer(
  ({navigation, route}: RouteNavigatorScreenProps<AppRoute.BRAND>) => {
    const {brandsStore, rangeStore, shopSelectionStore} = useStores();
    const {promotions: allPromotions} = usePromotions();
    const [linkedProducts, setLinkedProducts] = useState<Record<string, OrderLine[]> | null>(null);
    const brand = route.params.brand;

    useEffect(() => {
      // DIS-2005: a response for a superseded request must not overwrite the current one.
      let cancelled = false;
      if (brand?.brand && rangeStore.departments.length) {
        brandsStore.loadLinkedProductsForBrand(brand).then(
          linked => {
            if (!cancelled) {
              setLinkedProducts(linked);
            }
          },
          err => {
            logger.error('BrandDetailScreen', 'Error loading brand products', serializeError(err));
            if (!cancelled) {
              setLinkedProducts({});
            }
          },
        );
      }
      return () => {
        cancelled = true;
      };
    }, [brand, brandsStore, rangeStore.departments.length]);
    const loadingLinked =
      linkedProducts === null && !!brand?.brand && !shopSelectionStore.ecommerceDisabled;

    const [headlineVideoPaused, setHeadlineVideoPaused] = useState(true);
    const [articleVideoPaused, setArticleVideoPaused] = useState(true);
    const [youtubeVideoPaused, setYoutubeVideoPaused] = useState(true);

    const {isWebDesktopScreen} = useWebScreenDimensions();
    // DIS-1226: AnimatedTabBar is absolute-positioned on web mobile and overlays the
    // bottom ~52 px of this ScrollView, hiding the last linked-product tile's
    // Add-to-Cart button. Hook returns 0 on native and web desktop. Mirrors DIS-1127
    // fix on PromotionDetailScreen.
    const tabBarPadding = useTabBarBottomPadding();

    // This is to stop the video playing whenever we navigate away.
    useFocusEffect(
      React.useCallback(() => {
        // Do something when the screen is focused
        // component.current.startAutoplay(false);

        return () => {
          // Do something when the screen is unfocused
          // Useful for cleanup functions
          setHeadlineVideoPaused(true);
          setArticleVideoPaused(true);
          setYoutubeVideoPaused(true);
        };
      }, []),
    );

    const getImage = (detail, vidType, margin) => {
      return getFeedDiscoverDetailGraphics(
        detail,
        vidType,
        margin,
        null,
        setHeadlineVideoPaused,
        headlineVideoPaused,
        articleVideoPaused,
        youtubeVideoPaused,
      );
    };

    const promotions = allPromotions.filter(activation =>
      brand?.promotions?.includes(activation.id),
    );

    const handlePromotionPress = (promotion: AirtablePromotionItem) =>
      navigation.navigate(AppRoute.PROMOTION, {
        promotion: promotion,
        brand: brand,
      });

    return (
      <ContentView>
        <ScrollView
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{paddingBottom: tabBarPadding}}>
          <View style={styles.container}>
            {getImage(brand?.heroImage, HEADLINE_VIDEO)}
            <View style={styles.textCard}>
              <Text style={styles.headline}>{brand?.headline}</Text>
              <Text style={styles.copy}>{brand?.copy?.trimEnd()}</Text>
            </View>
          </View>
          {promotions.map(promotion => {
            const image = getHeroImageDetail(promotion, isWebDesktopScreen);
            return (
              <View key={promotion.id} style={styles.promotionImageHolder}>
                <Pressable onPress={() => handlePromotionPress(promotion)}>
                  <Image
                    source={{uri: image?.url}}
                    style={
                      isWebDesktopScreen ? styles.promotionImageWebDesktop : styles.promotionImage
                    }
                  />
                  <View style={styles.textHolder}>
                    <Text style={styles.headline}>
                      {promotion.tileHeadline || promotion.bannerHeadline || promotion.headline}
                    </Text>
                    {!!(promotion.firstCopy || promotion.description) && (
                      <Text style={styles.copyStyle} numberOfLines={2} adjustsFontSizeToFit={false}>
                        {promotion.firstCopy || promotion.description}
                      </Text>
                    )}
                  </View>
                </Pressable>
              </View>
            );
          })}

          {!shopSelectionStore.ecommerceDisabled &&
          linkedProducts &&
          Object.keys(linkedProducts).length ? (
            // DIS-1951: the promotion listing per department — 12 tiles, a "See all"
            // card and an inline grid of every line, instead of a rail cut at 8.
            Object.entries(linkedProducts).map(([key, value], index) => {
              const lines = value as OrderLine[];
              const count = lines.length;
              return (
                <View
                  testID={`brand-department-${key}`}
                  style={styles.productHolder}
                  key={`${key.replace(' ', '')}-${index}`}>
                  <PromotionProductListing
                    items={lines}
                    countLabel={`${key.toUpperCase()} · ${count} ${count === 1 ? 'PRODUCT' : 'PRODUCTS'}`}
                    sectionName={key}
                  />
                </View>
              );
            })
          ) : loadingLinked ? (
            <ActivityIndicator
              testID="brand-products-loading"
              size="large"
              style={styles.loadingIndicator}
            />
          ) : null}
        </ScrollView>
      </ContentView>
    );
  },
);

// DIS-2005: BRAND -> BRAND navigation keeps this screen mounted and swaps only the
// route params (TabNavigator `getId={() => name}`). Keying the content on the brand
// remounts it, so each brand starts with a new ScrollView at offset 0, no products
// until its own load resolves, and every rail at its first tile and collapsed.
const BrandDetailScreen = (props: RouteNavigatorScreenProps<AppRoute.BRAND>) => {
  const brand = props.route.params.brand;
  return <BrandDetailContent key={String(brand?.id ?? brand?.brand ?? brand)} {...props} />;
};

export default BrandDetailScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  imageContainer: {
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 5,
    },
    shadowOpacity: 0.29,
    shadowRadius: 4.65,
    elevation: 7,
  },
  topMargin: {
    marginTop: 16,
  },
  imageResponsive: {
    width: '100%',
    height: undefined,
    aspectRatio: 1,
  },

  textCard: {
    marginTop: 16,
  },
  headline: {
    fontSize: 20,
    color: theme.colour.text.heading.primary,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  copy: {
    fontSize: 16,
  },
  videoContainer: {
    borderRadius: 4,
    width: '100%',

    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 5,
    },
    shadowOpacity: 0.29,
    shadowRadius: 4.65,
    elevation: 7,
  },
  video: {
    borderRadius: 4,
    width: '100%',
    aspectRatio: 1920 / 1080,
  },
  youtube: {
    width: '100%',
    aspectRatio: 1920 / 1080,
    marginTop: 16,
    borderRadius: 4,
  },
  productHolder: {
    marginBottom: 4,
  },
  promotionImageHolder: {
    marginTop: 24,
    marginHorizontal: 8,
    backgroundColor: 'white',
    borderRadius: 4,
    borderWidth: 0.5,
    borderColor: '#CCC',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 5,
    },
    shadowOpacity: 0.29,
    shadowRadius: 4.65,
    elevation: 7,
    overflow: 'visible',
  },
  promotionImage: {
    width: '100%',
    aspectRatio: 760 / (300 + 200),
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  },
  promotionImageWebDesktop: {
    width: '100%',
    aspectRatio: 38 / 15,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  },
  textHolder: {
    width: '100%',
    backgroundColor: theme.colour.general.secondary,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  loadingIndicator: {
    color: theme.colour.activityIndicator.primary,
    marginTop: 32,
  },
});
